export const BRAND_REFERENCE_SELECTION_SOURCES = ["system_default", "user"] as const;
export type BrandReferenceSelectionSource = (typeof BRAND_REFERENCE_SELECTION_SOURCES)[number];

export const BRAND_REAL_PEOPLE_REFERENCE_POLICIES = [
  "style_and_setting_only",
  "allow_people",
] as const;
export type BrandRealPeopleReferencePolicy = (typeof BRAND_REAL_PEOPLE_REFERENCE_POLICIES)[number];

export type BrandReferenceAssetSourceType = "website" | "instagram" | "upload" | "generated";

export interface BrandReferenceCandidate {
  canonicalAssetId: string;
  sourceType: BrandReferenceAssetSourceType;
  imageClassification: string | null;
  imageClassificationConfidencePct: number | null;
  isVector: boolean;
  width: number | null;
  height: number | null;
  createdAt: Date;
}

const DEFAULT_REFERENCE_TOTAL = 10;
const DEFAULT_REFERENCE_TARGET_PER_SOURCE = 5;
const MIN_REFERENCE_DIMENSION = 512;

export const isPhotographicBrandReferenceClassification = (
  imageClassification: string | null,
): boolean =>
  imageClassification === "pure_photo" || imageClassification === "photo_with_graphics_or_text";

const isEligibleReferenceCandidate = (candidate: BrandReferenceCandidate): boolean =>
  isPhotographicBrandReferenceClassification(candidate.imageClassification) &&
  !candidate.isVector &&
  candidate.width !== null &&
  candidate.height !== null &&
  candidate.width >= MIN_REFERENCE_DIMENSION &&
  candidate.height >= MIN_REFERENCE_DIMENSION;

const candidateQualityScore = (candidate: BrandReferenceCandidate): number => {
  const width = candidate.width ?? 0;
  const height = candidate.height ?? 0;
  return width * height;
};

const sortReferenceCandidates = (
  candidates: BrandReferenceCandidate[],
): BrandReferenceCandidate[] =>
  candidates.toSorted((left, right) => {
    const confidenceDelta =
      (right.imageClassificationConfidencePct ?? -1) -
      (left.imageClassificationConfidencePct ?? -1);
    if (confidenceDelta !== 0) return confidenceDelta;

    const qualityDelta = candidateQualityScore(right) - candidateQualityScore(left);
    if (qualityDelta !== 0) return qualityDelta;

    const createdDelta = right.createdAt.getTime() - left.createdAt.getTime();
    if (createdDelta !== 0) return createdDelta;

    return right.canonicalAssetId.localeCompare(left.canonicalAssetId);
  });

const uniqueByCanonicalAssetId = (
  candidates: BrandReferenceCandidate[],
): BrandReferenceCandidate[] => {
  const seen = new Set<string>();
  const unique: BrandReferenceCandidate[] = [];
  for (const candidate of candidates) {
    if (seen.has(candidate.canonicalAssetId)) continue;
    seen.add(candidate.canonicalAssetId);
    unique.push(candidate);
  }
  return unique;
};

export const selectDefaultBrandReferenceAssetIds = (
  candidates: BrandReferenceCandidate[],
  existingSelectionIds: Iterable<string> = [],
): string[] => {
  const existing = new Set(existingSelectionIds);
  const remainingSlotCount = Math.max(0, DEFAULT_REFERENCE_TOTAL - existing.size);
  if (remainingSlotCount === 0) {
    return [];
  }

  const eligible = uniqueByCanonicalAssetId(
    sortReferenceCandidates(
      candidates.filter(
        (candidate) =>
          isEligibleReferenceCandidate(candidate) && !existing.has(candidate.canonicalAssetId),
      ),
    ),
  );

  const website = eligible.filter((candidate) => candidate.sourceType === "website");
  const instagram = eligible.filter((candidate) => candidate.sourceType === "instagram");

  const selected = new Map<string, BrandReferenceCandidate>();
  const add = (candidate: BrandReferenceCandidate): void => {
    if (selected.size >= remainingSlotCount) return;
    selected.set(candidate.canonicalAssetId, candidate);
  };

  for (const candidate of instagram.slice(0, DEFAULT_REFERENCE_TARGET_PER_SOURCE)) add(candidate);
  for (const candidate of website.slice(0, DEFAULT_REFERENCE_TARGET_PER_SOURCE)) add(candidate);

  const remaining = sortReferenceCandidates(
    eligible.filter((candidate) => !selected.has(candidate.canonicalAssetId)),
  );
  for (const candidate of remaining) add(candidate);

  return [...selected.keys()];
};
